import React, { useCallback, useState } from 'react';
import { useOwnerDetails } from 'api/owners';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';
import { Controller, useForm } from 'react-hook-form';

import { Form, message } from 'antd';
import { Button } from 'ui-kit';
import Wrapper from 'components/Wrapper';
import { Input } from 'components/Form';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';
import ConfirmModal from 'components/ConfirmModal';
import { ownerUpdateProfileDetails } from 'api/owner_sdk';

const validSchema = yup.object({
  firstName: yup.string().required('Please input enter a name.'),
  email: yup.string().email('Please enter a valid email.').required('Please enter a valid email.'),
  phone: yup
    .string()
    .nullable()
    .test('valid-phone-number', 'Please input a valid phone number.', (value) => {
      if (_.isEmpty(value) || !value) {
        return true;
      }

      return /^[0-9]{10,15}$/.test(value);
    }),
});

interface IFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface IPersonalDetailsForm {
  ownerId: number;
  initialData: IFormValues;
}

const PersonalDetailsForm = ({ ownerId, initialData }: IPersonalDetailsForm) => {
  const [showLoading, hideLoading] = useLoadingBackdrop();
  const [updateEmailConfirmationIsOpen, setUpdateEmailConfirmationIsOpen] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { isDirty, dirtyFields },
    getValues,
  } = useForm({
    mode: 'all',
    defaultValues: { ...initialData },
    resolver: yupResolver(validSchema),
  });

  const { data, invalidate } = useOwnerDetails(ownerId);

  const handleUpdateForm = useCallback(
    (values) => {
      showLoading();
      const payload = {
        id: data.actor.id,
        ...values,
      };

      ownerUpdateProfileDetails({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        phone: payload.phone || null,
      })
        .then(() => {
          hideLoading();
          message.success('Account information updated');
          invalidate();
        })
        .catch((error) => {
          hideLoading();
          message.error(error.message);
        });
    },
    [data, hideLoading, invalidate, showLoading],
  );

  const onSubmit = (values) => {
    if (_.has(dirtyFields, 'email') || initialData.email !== values.email) {
      setUpdateEmailConfirmationIsOpen(true);
    } else {
      handleUpdateForm(values);
    }
  };

  return (
    <>
      <Wrapper className="hc-settings subform">
        <Form name="settings-account" layout="vertical">
          <Controller
            control={control}
            name="firstName"
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <Form.Item
                required
                name={name}
                label="First Name"
                validateStatus={error ? 'error' : ''}
                help={error?.message}
                initialValue={value}
              >
                <Input className="" value={value} onChange={onChange} placeholder="First name..." />
              </Form.Item>
            )}
          />
          <Controller
            control={control}
            name="lastName"
            render={({ field: { name, value, onChange } }) => (
              <Form.Item name={name} label="Last Name" initialValue={value}>
                <Input className="" value={value} onChange={onChange} placeholder="Last name..." />
              </Form.Item>
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <Form.Item
                required
                name={name}
                label="Email"
                validateStatus={error ? 'error' : ''}
                help={error?.message}
                initialValue={value}
              >
                <Input className="" value={value} onChange={onChange} placeholder="Email..." />
              </Form.Item>
            )}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <Form.Item
                name={name}
                label="Phone number"
                normalize={(value) => value.replace(/\D/g, '')}
                validateStatus={error ? 'error' : ''}
                help={error?.message}
                initialValue={value}
              >
                <Input
                  className=""
                  placeholder="Phone number..."
                  value={value}
                  onChange={onChange}
                  maxLength={15}
                />
              </Form.Item>
            )}
          />
          <Form.Item noStyle shouldUpdate>
            {() => (
              <Button
                variant="contained"
                type="submit"
                fullWidth
                onClick={(data) => handleSubmit(onSubmit)(data)}
                disabled={!isDirty}
              >
                Update
              </Button>
            )}
          </Form.Item>
        </Form>
      </Wrapper>
      <ConfirmModal
        visible={updateEmailConfirmationIsOpen}
        confirmText="Continue"
        cancelText="Cancel"
        confirmBtnType="primary"
        onCancel={() => setUpdateEmailConfirmationIsOpen(false)}
        onConfirm={() => handleUpdateForm(getValues())}
        destroyOnClose
      >
        <p>The email address is changed. You will be logged out after you save the form. </p>
        <p>Are you sure you want to continue?</p>
      </ConfirmModal>
    </>
  );
};

export default PersonalDetailsForm;
